import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Trash2, UserPlus, Pencil } from 'lucide-react';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import type { PersonWithInvolvement } from '../types';

// ── Detail Panel ──

function PersonDetail({ person, onClose, onRefresh }: {
  person: PersonWithInvolvement;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const { isAdmin } = useAuth();
  const { t } = useT();
  const [creatingUser, setCreatingUser] = useState(false);
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);

  const handleCreateUser = async () => {
    setCreatingUser(true);
    try {
      await api.createUserFromPerson(person.id);
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div className="panel-slide-title">{person.name}</div>
          <button className="panel-slide-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="panel-slide-body">
          {/* Info */}
          <div className="detail-section">
            <div className="detail-label">{t('common.details')}</div>
            <div className="detail-row"><span className="detail-row-label">{t('people.firstName')}</span> {person.first_name || '—'}</div>
            <div className="detail-row"><span className="detail-row-label">{t('people.lastName')}</span> {person.last_name || '—'}</div>
            <div className="detail-row"><span className="detail-row-label">{t('common.email')}</span> {person.email || '—'}</div>
            <div className="detail-row">
              <span className="detail-row-label">{t('people.visibility')}</span>
              <span className="badge">{person.visibility === 'involved' ? t('people.involvedOnly') : t('people.allProjects')}</span>
            </div>
            <div className="detail-row">
              <span className="detail-row-label">{t('people.account')}</span>
              {person.user_id ? <span className="badge badge-ok">{t('people.linked')}</span> : <span className="text-ink-3">{t('people.noAccount')}</span>}
            </div>
          </div>

          {/* Labels */}
          {person.labels.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">{t('people.roles')}</div>
              <div className="detail-badges">
                {person.labels.map(l => <span key={l} className="badge">{l}</span>)}
              </div>
            </div>
          )}

          {/* Projects as Collaborator */}
          {person.projectsAsCollaborator.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">{t('people.projectsAsCollaborator')}</div>
              {person.projectsAsCollaborator.map(p => (
                <div key={p.projectId} className="detail-row">
                  <a
                    href={`#project?id=${p.projectId}`}
                    className="text-ink-1"
                  >
                    {p.projectName}
                  </a>
                  {p.allocation > 0 && <span className="text-ink-3 text-sm">{p.allocation}%</span>}
                </div>
              ))}
            </div>
          )}

          {/* Projects as Sponsor */}
          {person.projectsAsSponsor.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">{t('people.projectsAsSponsor')}</div>
              {person.projectsAsSponsor.map(p => (
                <div key={p.projectId} className="detail-row">
                  <a
                    href={`#project?id=${p.projectId}`}
                    className="text-ink-1"
                  >
                    {p.projectName}
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Create User */}
          {isAdmin && !person.user_id && person.email && (
            <div className="detail-section">
              <button
                className="btn btn-accent btn-sm"
                onClick={() => setShowCreateConfirm(true)}
                disabled={creatingUser}
              >
                <UserPlus size={14} />
                {creatingUser ? t('people.creating') : t('people.createUserAccount')}
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={showCreateConfirm}
        title={t('people.createUserAccount')}
        message={t('people.createUserConfirm', { name: person.name, email: person.email || '' })}
        confirmLabel={t('people.createAccount')}
        onConfirm={() => { setShowCreateConfirm(false); handleCreateUser(); }}
        onCancel={() => setShowCreateConfirm(false)}
      />
    </div>
  );
}

// ── Add/Edit Modal ──

function PersonModal({ person, onClose, onSave }: {
  person: PersonWithInvolvement | null; // null = add mode
  onClose: () => void;
  onSave: () => void;
}) {
  const { isAdmin } = useAuth();
  const { t } = useT();
  const [firstName, setFirstName] = useState(person?.first_name || '');
  const [lastName, setLastName] = useState(person?.last_name || '');
  const [email, setEmail] = useState(person?.email || '');
  const [visibility, setVisibility] = useState(person?.visibility || 'all');
  const [emailPattern, setEmailPattern] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getSettings().then((settings: any) => {
      if (settings.email_pattern) setEmailPattern(settings.email_pattern);
    }).catch(() => {});
  }, []);

  // Auto-generate email for new person
  useEffect(() => {
    if (!person && firstName && lastName && emailPattern) {
      const generated = emailPattern
        .replace('{firstname}', firstName.toLowerCase().trim())
        .replace('{lastname}', lastName.toLowerCase().trim());
      setEmail(generated);
    }
  }, [firstName, lastName, emailPattern, person]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) { setError(t('people.firstNameRequired')); return; }

    setSaving(true);
    setError('');

    try {
      if (person) {
        await api.updatePerson(person.id, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim() || null,
          visibility,
        });
      } else {
        await api.createFullPerson({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim() || undefined,
        });
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{person ? t('people.editPerson') : t('people.addPerson')}</h3>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body flex flex-col gap-12">
            <div className="form-group">
              <label className="form-label">{t('people.firstName')}</label>
              <input
                className="form-input"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder={t('people.firstNamePlaceholder')}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('people.lastName')}</label>
              <input
                className="form-input"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder={t('people.lastNamePlaceholder')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('common.email')}</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('people.emailPlaceholder')}
              />
            </div>
            {isAdmin && (
              <div className="form-group">
                <label className="form-label">{t('people.visibility')}</label>
                <div className="selectable-group">
                  <button
                    type="button"
                    className={`selectable ${visibility === 'all' ? 'active' : ''}`}
                    onClick={() => setVisibility('all')}
                  >
                    {t('people.allProjects')}
                  </button>
                  <button
                    type="button"
                    className={`selectable ${visibility === 'involved' ? 'active' : ''}`}
                    onClick={() => setVisibility('involved')}
                  >
                    {t('people.involvedOnly')}
                  </button>
                </div>
              </div>
            )}
            {error && <div className="text-sm text-err">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-accent" disabled={saving}>
              {saving ? t('common.saving') : (person ? t('people.saveChanges') : t('people.addPerson'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── People Page ──

export function PeoplePage() {
  const { isAdmin } = useAuth();
  const { t } = useT();
  const [people, setPeople] = useState<PersonWithInvolvement[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<PersonWithInvolvement | null>(null);
  const [modalPerson, setModalPerson] = useState<PersonWithInvolvement | null | undefined>(undefined); // undefined=closed, null=add, PersonWithInvolvement=edit
  const [deleteTarget, setDeleteTarget] = useState<{ id: number } | null>(null);

  const loadData = useCallback(async () => {
    const data = await api.getInventoryPeople() as PersonWithInvolvement[];
    setPeople(data);
    setLoading(false);

    // Refresh selected person if panel is open
    if (selectedPerson) {
      const updated = data.find(p => p.id === selectedPerson.id);
      if (updated) setSelectedPerson(updated);
    }
  }, [selectedPerson?.id]);

  useEffect(() => { loadData(); }, []);

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.deletePerson(deleteTarget.id);
      setDeleteTarget(null);
      setSelectedPerson(null);
      loadData();
    } catch (err: any) {
      setDeleteTarget(null);
      alert(err.message);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return people;
    const q = search.toLowerCase();
    return people.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      p.labels.some(l => l.toLowerCase().includes(q))
    );
  }, [people, search]);

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('people.title')}</h1>
          <p className="page-meta">{filtered.length === 1 ? t('people.personOne', { n: filtered.length }) : t('people.personMany', { n: filtered.length })}</p>
        </div>
        <button className="btn btn-accent btn-sm" onClick={() => setModalPerson(null)}>
          <UserPlus size={14} /> {t('people.addPerson')}
        </button>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={t('people.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('people.emptyTitle')}</div>
          <div className="empty-state-desc">{search ? t('people.emptyDescSearch') : t('people.emptyDescDefault')}</div>
        </div>
      ) : (
        <table className="table table-sticky">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.email')}</th>
              <th>{t('people.roles')}</th>
              <th>{t('people.projects')}</th>
              <th>{t('people.visibility')}</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr
                key={p.id}
                className={`list-row-clickable ${selectedPerson?.id === p.id ? 'list-row-selected' : ''}`}
                onClick={() => setSelectedPerson(p)}
              >
                <td>
                  <span className="font-medium text-ink-1">{p.name}</span>
                  {p.user_id && <span className="badge badge-ok">{t('people.userBadge')}</span>}
                </td>
                <td className="text-sm text-ink-2">{p.email || '—'}</td>
                <td>
                  {p.labels.length > 0
                    ? p.labels.map(l => <span key={l} className="badge">{l}</span>)
                    : <span className="text-ink-3">—</span>
                  }
                </td>
                <td className="text-sm tabular-nums">
                  {p.projectCount > 0 ? p.projectCount : '—'}
                </td>
                <td>
                  <span className="badge">
                    {p.visibility === 'involved' ? t('people.visibilityInvolved') : t('people.visibilityAll')}
                  </span>
                </td>
                <td>
                  <button className="btn-icon" title={t('common.edit')} onClick={(e) => { e.stopPropagation(); setModalPerson(p); }}>
                    <Pencil className="icon-sm" />
                  </button>
                  <button className="btn-icon" title={t('common.delete')} onClick={(e) => handleDelete(p.id, e)}>
                    <Trash2 className="icon-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Detail panel */}
      {selectedPerson && (
        <PersonDetail
          person={selectedPerson}
          onClose={() => setSelectedPerson(null)}
          onRefresh={loadData}
        />
      )}

      {/* Add/Edit modal */}
      {modalPerson !== undefined && (
        <PersonModal
          person={modalPerson}
          onClose={() => setModalPerson(undefined)}
          onSave={loadData}
        />
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title={t('people.deletePerson')}
        message={t('people.deletePersonMessage')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
